
<!--
  - Copyright (c) 2022 by Leyton Group - All Rights Reserved.
  - This source code, and resulting software, is the confidential and proprietary information
  - ("Proprietary Information") and is the intellectual property ("Intellectual Property")
  - of Leyton Group, Inc. ("The Company"). You shall not disclose such Proprietary Information
  - and shall use it only in accordance with the terms and conditions of any and all license
  - agreements you have entered into with The Company.
  -->

<template>
<div id="Profile">
  <div class="giga-content_container">
    <div class="row">
      <div class="col-xl-7 col-lg-6">
        <div class="giga-card giga-card_equal_height">
          <div class="giga-card_wrap">
            <h3 class="giga-card_title">{{ $t("user_profile") }}</h3>
            <div class="giga-card_content giga-card_flex">

              <div class="giga-card_user_profile">
                <div class="giga-card_user_thumb">
                  <img v-if="userForm.avatar" :src="userForm.avatar" alt="avatar"/>
                  <img v-else src="@/assets/images/contact-mask.svg" alt="avatar"/>
                </div>
              </div>

              <div class="giga-card_user_actions">
                <div class="giga-flex_buttons buttons-full_width">
                  <div class="giga-button">
                    <input type="file" accept="image/jpeg, image/png" @change="uploadAvatar($event)" style="display: none" ref="imgInput">
                    <b-button
                      class="giga-blue_button no-icon"
                      @click="$refs.imgInput.click()"
                    >{{ $t("edit") }}
                    </b-button>
                  </div>
                  <div class="giga-button">
                    <b-button
                      class="giga-blue_outlined_button no-icon"
                      @click="deleteAvatar()"
                    >{{ $t("delete") }}</b-button>
                  </div>
                </div>

                <div class="giga-info_box">
                  <i class="info-icon">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M10 0C4.475 0 0 4.475 0 10C0 15.525 4.475 20 10 20C15.525 20 20 15.525 20 10C20 4.475 15.525 0 10 0ZM11.25 3.75V6.25H8.75V3.75H11.25ZM7.5 15V16.25H12.5V15H11.25V7.5H7.5V8.75H8.75V15H7.5Z" fill="#132D48"/>
                    </svg>
                  </i>
                  <p>Les chargements doivent être au format jpg ou png et ne doivent pas dépasser 2 Mo.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-5 col-lg-6">
        <div class="giga-card giga-card_equal_height">
          <div class="giga-card_wrap">
            <h3 class="giga-card_title">Sécurité</h3>
            <div class="giga-card_content">
              <div class="giga-fields">
                <div class="giga-input_field full-field"
                     :class="{'field-hasError': $v.changePassword.newPassword.$invalid}">
                  <label class="input-field_label">Nouveau mot de passe</label>
                  <div class="input-field_area">
                      <input :type="passwordFieldType" v-model="$v.changePassword.newPassword.$model">
                      <button class="togglePassword" v-if="passwordFieldType == 'password'" @click="passwordVisibitly">
                        <svg width="22" height="16" viewBox="0 0 22 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M11 0.5C6 0.5 1.73 3.61 0 8C1.73 12.39 6 15.5 11 15.5C16 15.5 20.27 12.39 22 8C20.27 3.61 16 0.5 11 0.5ZM11 13C8.24 13 6 10.76 6 8C6 5.24 8.24 3 11 3C13.76 3 16 5.24 16 8C16 10.76 13.76 13 11 13ZM11 5C9.34 5 8 6.34 8 8C8 9.66 9.34 11 11 11C12.66 11 14 9.66 14 8C14 6.34 12.66 5 11 5Z" fill="#132D48"/>
                        </svg>
                      </button>
                      <button class="togglePassword" v-if="passwordFieldType == 'text'" @click="passwordVisibitly">hide</button>
                  </div>
                  <div class="giga-login_errors"  v-if="$v.changePassword.newPassword.$anyDirty && $v.changePassword.newPassword.$invalid ">
                    <p v-if="!$v.changePassword.newPassword.pattern">{{$t('password_must_include')}}</p>
                    <ul v-if="!$v.changePassword.newPassword.pattern" class="login-errors_list">
                      <li class="error-item">{{ $tc('min_characters' , 8) }}</li>
                      <li class="error-item">{{ $tc('min_uppercase', 1) }}</li>
                      <li class="error-item">{{ $tc('min_lowercase', 1) }}</li>
                      <li class="error-item">{{ $tc('min_number', 1) }}</li>
                      <li class="error-item">{{ $tc('special_character', 1) }}</li>
                    </ul>
                  </div>

                </div>
                <div class="giga-input_field full-field"
                     :class="{'field-hasError': $v.changePassword.confirmPassword.$anyDirty && $v.changePassword.confirmPassword.$invalid}">
                  <label class="input-field_label">Confirmer votre mot de passe</label>
                  <div class="input-field_area">
                      <input :type="confirmPasswordFieldType" v-model="$v.changePassword.confirmPassword.$model">
                      <button class="togglePassword" v-if="confirmPasswordFieldType == 'password'" @click="confirmPasswordVisibitly">
                        <svg width="22" height="16" viewBox="0 0 22 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path fill-rule="evenodd" clip-rule="evenodd" d="M11 0.5C6 0.5 1.73 3.61 0 8C1.73 12.39 6 15.5 11 15.5C16 15.5 20.27 12.39 22 8C20.27 3.61 16 0.5 11 0.5ZM11 13C8.24 13 6 10.76 6 8C6 5.24 8.24 3 11 3C13.76 3 16 5.24 16 8C16 10.76 13.76 13 11 13ZM11 5C9.34 5 8 6.34 8 8C8 9.66 9.34 11 11 11C12.66 11 14 9.66 14 8C14 6.34 12.66 5 11 5Z" fill="#132D48"/>
                        </svg>
                      </button>
                      <button class="togglePassword" v-if="confirmPasswordFieldType == 'text'" @click="confirmPasswordVisibitly">hide</button>
                  </div>
                  <div class="giga-login_errors"  v-if="$v.changePassword.confirmPassword.$anyDirty && $v.changePassword.confirmPassword.$invalid">
                    <ul  v-if="!$v.changePassword.confirmPassword.sameAsPassword" class="login-errors_list">
                      <li class="error-item">{{$t('password_must_be_identical')}}</li>
                    </ul>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-12 col-lg-12">
        <div class="giga-card giga-card_equal_height giga-margin-t_35">
          <div class="giga-card_wrap">
            <h3 class="giga-card_title">Informations personnelles</h3>
            <div class="giga-card_content">
              <div class="giga-fields">
                <div class="giga-input_field half-field"
                     :class="{'field-hasError': $v.userForm.lastName.$anyDirty && $v.userForm.lastName.$invalid}">
                    <label class="input-field_label">{{$t('your_last_name')}}</label>
                    <div class="input-field_area">
                        <input type="text" id="last_name" v-model="$v.userForm.lastName.$model">
                    </div>
                </div>
                <div class="giga-input_field half-field"
                     :class="{'field-hasError': $v.userForm.firstName.$anyDirty && $v.userForm.firstName.$invalid}">
                    <label class="input-field_label">{{$t('your_first_name')}}</label>
                    <div class="input-field_area">
                        <input type="text" id="first_name" v-model="$v.userForm.firstName.$model">
                    </div>
                </div>
                <div class="giga-input_field half-field"
                     :class="{'field-hasError': $v.userForm.email.$anyDirty && $v.userForm.email.$invalid}">
                    <label class="input-field_label">{{$t('email')}}</label>
                    <div class="input-field_area">
                        <input type="text" id="email" v-model="$v.userForm.email.$model">
                    </div>
                </div>
                <div class="giga-input_field half-field"
                     :class="{'field-hasError': $v.userForm.phoneNumber.$anyDirty && $v.userForm.phoneNumber.$invalid}">
                    <label class="input-field_label">{{$t('phone')}}</label>
                    <div class="input-field_area">
                        <input type="text" id="phone" v-model="$v.userForm.phoneNumber.$model">
                    </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="giga-document_footer giga-margin-t_65 giga-flex giga-flex_end">
      <div class="giga-button">
        <b-button :disabled="$v.userForm.$invalid
        || ($v.changePassword.$anyDirty && $v.changePassword.$invalid)
        || !$v.$anyDirty"
                  class="giga-green_button no-icon"
                  @click="updateAccount()">
          <span v-if="!isUpdating">{{$t("update")}}</span>
          <b-spinner v-else variant="light"></b-spinner>
        </b-button>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import {mapGetters} from "vuex";
import {email, maxLength, minLength, required, requiredIf, sameAs} from "vuelidate/lib/validators";
import {constants} from "@/shared/constants";
import {userService} from "@/views/service/user.service";


export default {
  name: "Profile",
  data: function () {
    return {
      userForm: {
        firstName: '',
        lastName: '',
        phoneNumber: '',
        email: '',
        avatar: null
      },
      changePassword: {
        newPassword: '',
        confirmPassword: '',
      },

      passwordFieldType: "password",
      confirmPasswordFieldType: "password",
      isUpdating: false
    }
  },
  validations: {
    changePassword: {
      newPassword: {
        minLength: minLength(constants.MIN_PASSWORD_LENGTH),
        maxLength: maxLength(constants.MAX_PASSWORD_LENGTH),
        pattern: constants.passwordPattern,
      },
      confirmPassword: {
        sameAsPassword: sameAs((vm) => {
          return vm.newPassword;
        }),
      },
    },
    userForm: {
      lastName: {
        required
      },
      firstName: {
        required
      },
      email: {
        required,
        email
      },
      phoneNumber: {
        phone: constants.phonePattern
      },
      avatar: {
      }
    }
  },
  computed: {
    ...mapGetters("account", ["getCurrentUser"]),
  },
  mounted() {
    this.userForm = {
      firstName: this.getCurrentUser.firstName,
      lastName: this.getCurrentUser.lastName,
      email: this.getCurrentUser.email,
      phoneNumber: this.getCurrentUser.phoneNumber,
      avatar: this.getCurrentUser.avatar,
    };
  },
  methods:{
    passwordVisibitly(){
      this.passwordFieldType = this.passwordFieldType === "password" ? "text" : "password";
    },
    confirmPasswordVisibitly(){
      this.confirmPasswordFieldType = this.confirmPasswordFieldType === "password" ? "text" : "password";
    },
    deleteAvatar() {
      this.$v.userForm.avatar.$touch();
      this.userForm.avatar = '';
    },
    uploadAvatar(event) {
      var file = event.target.files[0];
      var reader = new FileReader();
      this.$v.userForm.avatar.$touch();
      reader.onloadend = function() {
        this.userForm.avatar = reader.result;
      }.bind(this);
      reader.readAsDataURL(file);
    },
    updateAccount() {
      let dto = {}
      if (this.$v.userForm.$anyDirty) {
        this.updateUserInfo(dto);
      }
      if (this.$v.changePassword.$anyDirty) {
        this.updatePassword(dto);
      }
      this.isUpdating = true;
      userService.updateProfile(dto).then(value => this.isUpdating = false);
      this.$v.$reset();
    },
    updateUserInfo(dto) {
      for (let key in this.userForm) {
        if (this.$v.userForm[key].$anyDirty) {
          dto[key] = this.userForm[key];
        }
      }
    },
    updatePassword(dto) {
      if (this.$v.changePassword.$anyDirty &&
          !this.$v.changePassword.$invalid &&
          this.changePassword.newPassword) {
        dto.newPassword = this.changePassword.newPassword;
        dto.confirmPassword = this.changePassword.confirmPassword;
      }
    },
  }
}
</script>

<style scoped>

</style>
